import { useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/layout/DashboardLayout';
import { ConfirmDialog } from '@/design-system';
import { useErrorToast } from '@/core/errors/useErrorToast';
import type { Plan } from './types';
import { usePlansListQuery } from './hooks/usePlansListQuery';
import { usePlanMutations } from './hooks/usePlanMutations';
import { PlanEditorDialog } from './components/PlanEditorDialog';
import { PlansTable } from './components/PlansTable';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { plansService } from './api/plans.service';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { useAuth } from '@/core/auth';

const PAGE_LIMIT = 20;

export function PlansPage() {
  const { user } = useAuth();
  const canManagePlans = user?.role === 'SUPERADMIN';
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPlanId = searchParams.get('edit');
  const requestedPlan = useQuery({
    queryKey: queryKeys.plans.detail(requestedPlanId ?? ''),
    queryFn: () => plansService.get(requestedPlanId!),
    enabled: !!requestedPlanId && canManagePlans,
  });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, isActiveFilter]);

  const listFilters = useMemo(
    () => ({
      page,
      limit: PAGE_LIMIT,
      search: debouncedSearch || undefined,
      isActive: isActiveFilter,
    }),
    [page, debouncedSearch, isActiveFilter]
  );

  const { data, isLoading, isError, error } = usePlansListQuery(listFilters);
  const onErrorToast = useErrorToast();

  useEffect(() => {
    if (!canManagePlans || !requestedPlanId || !requestedPlan.data) return;
    setSelectedPlan(requestedPlan.data);
    setShowEdit(true);
  }, [requestedPlanId, requestedPlan.data, canManagePlans]);

  useEffect(() => {
    if (requestedPlan.error) onErrorToast(requestedPlan.error, 'Erro ao carregar o plano selecionado');
  }, [requestedPlan.error, onErrorToast]);

  useEffect(() => {
    if (isError && error) {
      onErrorToast(error, 'Erro ao carregar planos');
    }
  }, [isError, error, onErrorToast]);

  const plans = data?.data ?? [];
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const total = pagination?.total ?? 0;

  const planMutations = usePlanMutations();
  const { createPlan, updatePlan, deletePlan } = planMutations;

  const openEdit = useCallback((plan: Plan) => {
    setSelectedPlan(plan);
    setShowEdit(true);
  }, []);

  const openDelete = useCallback((plan: Plan) => {
    setSelectedPlan(plan);
    setShowDelete(true);
  }, []);

  const closeEditor = useCallback(() => {
    setShowCreate(false);
    setShowEdit(false);
    setSelectedPlan(null);
    setSearchParams((params) => { const next = new URLSearchParams(params); next.delete('edit'); return next; }, { replace: true });
  }, [setSearchParams]);

  const handlers = useMemo(
    () => ({
      onEdit: openEdit,
      onDelete: openDelete,
      onSyncStripe: (plan: Plan) => {
        planMutations.syncStripe.mutate(plan);
      },
    }),
    [openEdit, openDelete, planMutations]
  );

  return (
    <DashboardLayout title="Planos">
      <PlansTable
        plans={plans}
        isLoading={isLoading}
        search={search}
        onSearchChange={setSearch}
        page={page}
        totalPages={totalPages}
        total={total}
        limit={PAGE_LIMIT}
        onPageChange={setPage}
        isActiveFilter={isActiveFilter}
        onIsActiveFilterChange={setIsActiveFilter}
        onNewPlan={() => {
          setSelectedPlan(null);
          setShowCreate(true);
        }}
        handlers={handlers}
        canManagePlans={canManagePlans}
      />

      <PlanEditorDialog
        isOpen={canManagePlans && (showCreate || showEdit)}
        mode={showCreate ? 'create' : 'edit'}
        plan={showEdit ? selectedPlan : null}
        onClose={closeEditor}
        mutations={{ createPlan, updatePlan, toggleActive: planMutations.toggleActive }}
      />

      <ConfirmDialog
        isOpen={canManagePlans && showDelete}
        onClose={() => {
          setShowDelete(false);
          setSelectedPlan(null);
        }}
        onConfirm={() => {
          if (!selectedPlan) return;
          void deletePlan.mutateAsync(selectedPlan.id).then(() => {
            setShowDelete(false);
            setSelectedPlan(null);
          });
        }}
        title="Deletar Plano"
        message={`Tem certeza que deseja deletar o plano "${selectedPlan?.name}"?`}
        confirmText="Deletar"
        cancelText="Cancelar"
        variant="danger"
        isLoading={deletePlan.isPending}
      />
    </DashboardLayout>
  );
}
